import { FC } from 'react';
import { ICaremapNote } from '../notes/model/NotesClient';
import { CommunityAvatar } from './CommunityAvatar';
import { Text } from '../components/Text';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { formatRelativeTime } from '../utils/format';
import { CommunityPostMenu } from './CommunityPostMenu';
import { IRecording } from '../appointments/recording/RecordingState';

const useStyles = createUseStyles({
    avatarRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        paddingTop: 12
    },

    postInfo: {
        marginTop: 4,
        marginLeft: 8,
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column'
    },

    content: {
        marginTop: 4,
        display: '-webkit-box',
        // .sc-ion-label-md-s p sets a smaller font-size than we want
        fontSize: 'inherit !important'
    },

    truncatedContent: {
        overflow: 'hidden !important',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 10,
        // TODO: not needed anymore?? majority of browsers support -webkit-line-clamp
        // https://caniuse.com/css-line-clamp
        // for non-webkit browsers (Firefox)
        //maxHeight: '11.2rem',
    },

    smallText: {
        fontSize: 14
    },

    roleOrSection: {
        textTransform: 'capitalize',
        color: 'var(--ion-color-step-400)'
    }
});

export type ICommunityPostProps = {
    post: ICaremapNote;
    fullContent?: boolean;
};

export const CommunityPost: FC<ICommunityPostProps> = ({ post, fullContent }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);

    const recording = post.otherAttributes.recording as IRecording | undefined;
    const content = recording?.summary?.shortSummary || post.html;

    const getPostDate = () => {
        return formatRelativeTime(post.createdAt);
    };

    const getPostTitle = () => {
        const noteTarget = post.otherAttributes.noteTarget;
        const {member} = careMapState.getSupportGroupMemberInformation(noteTarget.memberId);
        const roleOrSection = member ? member.role : noteTarget.section !== 'patient' ? noteTarget.section : '';

        return <>
            <>
                {post.title ? <>{post.title}&nbsp;&nbsp;</> : ''}
                <span className={classes.roleOrSection}>{roleOrSection}</span>
            </>
        </>;
    };

    return (
        <div style={{width: '100%'}}>
            <div className={classes.avatarRow}>
                <CommunityAvatar
                    pictureUrl={post.createdByUser?.pictureUrl}
                    firstName={post.createdByUser?.firstName}
                    lastName={post.createdByUser?.lastName}
                />
                <div className={classes.postInfo}>
                    <Text type="subtitle">
                        <>{post.createdByUser?.firstName || 'Anonymous'}&nbsp;{post.createdByUser?.lastName}</>
                    </Text>
                    <div style={{marginTop: 5}} className={classes.smallText}>
                        <Text type="detail">{getPostDate()}</Text>
                    </div>
                </div>
                <CommunityPostMenu postId={post.noteId} />
            </div>
            <div style={{marginTop: 12, marginLeft: 4}}>
                <Text type="subtitle">
                    {getPostTitle()}
                </Text>
                <p
                    data-type="content"
                    className={`${classes.content} ${!fullContent ? classes.truncatedContent : ''}`}
                    dangerouslySetInnerHTML={{ __html: content.split('\n').join('<br/>')}}
                />
            </div>
        </div>
    );
};
